/**
 * Airplanes.live API Client
 *
 * Free, real-time ADS-B aircraft tracking data.
 * No authentication required, generous rate limits.
 * Endpoint: https://api.airplanes.live/v2/point/{lat}/{lon}/{radius_nm}
 *
 * Docs: https://airplanes.live/api-guide/
 */

export interface ADSBAircraft {
  hex: string;               // ICAO24 hex identifier
  flight: string | null;     // Callsign / flight number
  registration: string | null; // Aircraft registration (e.g. N12345)
  aircraftType: string | null; // ICAO type designator (e.g. B738, A320)
  latitude: number;
  longitude: number;
  altitudeBaro: number | null; // Barometric altitude in feet
  altitudeGeom: number | null; // Geometric altitude in feet
  groundSpeed: number | null;  // Ground speed in knots
  track: number | null;        // Track angle in degrees (0=north)
  baroRate: number | null;     // Vertical rate in ft/min
  squawk: string | null;       // Transponder code
  category: string | null;     // ADS-B emitter category (A0-D7)
  onGround: boolean;
  lastSeen: number;            // Seconds since last message
  distance: number | null;     // Distance from query point in nm
}

/**
 * Fetch live ADS-B aircraft within a radius of a center point.
 * @param lat Center latitude
 * @param lon Center longitude
 * @param radiusNm Radius in nautical miles (max 250)
 */
export async function fetchADSBAircraft(
  lat: number,
  lon: number,
  radiusNm: number = 100,
): Promise<ADSBAircraft[]> {
  const clampedRadius = Math.min(250, Math.max(1, Math.round(radiusNm)));
  const url = `https://api.airplanes.live/v2/point/${lat.toFixed(4)}/${lon.toFixed(4)}/${clampedRadius}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`airplanes.live API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (!data.ac || !Array.isArray(data.ac)) {
    return [];
  }

  return data.ac
    .filter((a: any) => a.lat != null && a.lon != null)
    .map((a: any): ADSBAircraft => ({
      hex: a.hex || '',
      flight: a.flight?.trim() || null,
      registration: a.r || null,
      aircraftType: a.t || null,
      latitude: a.lat,
      longitude: a.lon,
      altitudeBaro: a.alt_baro === 'ground' ? 0 : (a.alt_baro ?? null),
      altitudeGeom: a.alt_geom ?? null,
      groundSpeed: a.gs ?? null,
      track: a.track ?? null,
      baroRate: a.baro_rate ?? null,
      squawk: a.squawk ?? null,
      category: a.category ?? null,
      onGround: a.alt_baro === 'ground' || a.ground === true,
      lastSeen: a.seen ?? 0,
      distance: a.dst ?? null,
    }));
}
